import { useState } from "react";

import { isSharedEmployeeProfile } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/helpers/accessLevelFilters";
import { useCreateContactProfileMutation } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/queries/useCreateContactProfileMutation";
import { useDeleteContactProfileMutation } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/queries/useDeleteContactProfileMutation";
import { useUpdateContactProfileMutation } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/queries/useUpdateContactProfileMutation";
import type { ProfileDto } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/types/dto";
import ProfileModal from "app/pages/sales-intelligence/sub-modules/profile-modal/ProfileModal";
import { sentryErrorTrackerWithScope } from "app/pages/sales-intelligence/utils/tracking/sentry";

import { DeleteEmployeeProfileModal } from "../components/DeleteEmployeeProfileModal/DeleteEmployeeProfileModal";
import { useEmployeeProfilesTrackingService } from "../hooks/useEmployeeProfilesTrackingService";
import { useEmployeeProfileToasts } from "../hooks/useEmployeeProfileToasts";
import { getDeleteProfileErrorMessage } from "../utils/getDeleteProfileErrorMessage";

type EmployeeProfileModalContainerProps = {
    isOpen: boolean;
    /** Only relevant while creating; an edited profile keeps the visibility it was created with. */
    isSharedWithAccount: boolean;
    profileForEdit: ProfileDto | null;
    onClose: () => void;
};

export const EmployeeProfileModalContainer = ({
    isOpen,
    isSharedWithAccount,
    profileForEdit,
    onClose,
}: EmployeeProfileModalContainerProps) => {
    const [isDeleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

    const employeeProfilesTracking = useEmployeeProfilesTrackingService();
    const { showSuccess, showError, showErrorMessage } = useEmployeeProfileToasts();

    const { mutateAsync: createProfile, isPending: isCreating } = useCreateContactProfileMutation();
    const { mutateAsync: updateProfile, isPending: isUpdating } = useUpdateContactProfileMutation();
    const { mutateAsync: deleteProfile, isPending: isDeleting } = useDeleteContactProfileMutation();

    const handleSubmit = async (payload: Omit<ProfileDto, "id" | "isShared">) => {
        const isCreate = profileForEdit === null;
        employeeProfilesTracking.trackProfileSubmit(isCreate ? "create" : "edit");

        try {
            if (isCreate) {
                await createProfile({ ...payload, isShared: isSharedWithAccount });
                showSuccess("si.employee-profiles.toast.created");
            } else {
                await updateProfile({
                    ...payload,
                    id: profileForEdit.id,
                    isShared: isSharedEmployeeProfile(profileForEdit),
                });
                showSuccess("si.employee-profiles.toast.saved");
            }
            onClose();
        } catch (error) {
            sentryErrorTrackerWithScope({
                errorMessage: isCreate ? "Failed to create employee profile" : "Failed to update employee profile",
                e: error,
                tags: { component: "employee-profiles" },
                params: { extra: { profileId: profileForEdit?.id } },
            });
            showError();
        }
    };

    const handleDeleteClick = () => {
        employeeProfilesTracking.trackProfileDeleteFromModalOpen();
        setDeleteConfirmOpen(true);
    };

    const handleDeleteCancel = () => {
        employeeProfilesTracking.trackDeleteCancel();
        setDeleteConfirmOpen(false);
    };

    const handleDeleteConfirm = async () => {
        if (!profileForEdit) {
            return;
        }

        employeeProfilesTracking.trackDeleteConfirm();

        try {
            await deleteProfile(profileForEdit.id);
            setDeleteConfirmOpen(false);
            onClose();
        } catch (error) {
            const serverMessage = await getDeleteProfileErrorMessage(error);

            // A rejected delete is an expected outcome rather than a fault, so it is not reported to Sentry.
            if (serverMessage) {
                showErrorMessage(serverMessage);
                return;
            }

            sentryErrorTrackerWithScope({
                errorMessage: "Failed to delete employee profile from the edit modal",
                e: error,
                tags: { component: "employee-profiles" },
                params: { extra: { profileId: profileForEdit.id } },
            });
            showError();
        }
    };

    const isProfileModalVisible = isOpen && !isDeleteConfirmOpen;

    return (
        <>
            <ProfileModal
                isOpened={isProfileModalVisible}
                profile={profileForEdit}
                isBeingDeleted={isDeleting}
                isBeingSubmitted={isCreating || isUpdating}
                createTitleI18nKey="si.employee-profiles.modal.create.title"
                editTitleI18nKey="si.employee-profiles.modal.edit.title"
                descriptionI18nKey="si.employee-profiles.modal.description"
                onCancel={onClose}
                onSubmit={handleSubmit}
                onDeleteClick={handleDeleteClick}
            />

            {profileForEdit ? (
                <DeleteEmployeeProfileModal
                    isOpen={isDeleteConfirmOpen}
                    isShared={isSharedEmployeeProfile(profileForEdit)}
                    profileName={profileForEdit.name}
                    isDeleting={isDeleting}
                    onCancel={handleDeleteCancel}
                    onConfirm={() => void handleDeleteConfirm()}
                />
            ) : null}
        </>
    );
};
